import React, { useCallback, useEffect, useState } from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { RangeFilterConfig } from "@/types/metrics/metadata"

type RangeMode = RangeFilterConfig["mode"]

const RANGE_MODES: { value: RangeMode; label: string }[] = [
  { value: "first_n", label: "First" },
  { value: "last_n", label: "Last" },
]

interface RangeFilterEditorProps {
  filter: RangeFilterConfig
  onChange: (filter: RangeFilterConfig) => void
}

export const RangeFilterEditor: React.FC<RangeFilterEditorProps> = ({
  filter,
  onChange,
}) => {
  const [localCount, setLocalCount] = useState(filter.count.toString())

  useEffect(() => {
    setLocalCount(filter.count.toString())
  }, [filter.count])

  const handleModeChange = useCallback(
    (value: string) => {
      onChange({ ...filter, mode: value as RangeMode })
    },
    [filter, onChange],
  )

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalCount(value)
    const parsed = parseInt(value, 10)
    if (!isNaN(parsed) && parsed > 0) {
      onChange({ ...filter, count: parsed })
    }
  }

  const handleCountBlur = () => {
    const parsed = parseInt(localCount, 10)
    if (isNaN(parsed) || parsed < 1) {
      setLocalCount("1")
      onChange({ ...filter, count: 1 })
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={filter.mode} onValueChange={handleModeChange}>
        <SelectTrigger className="w-24" aria-label="Range mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RANGE_MODES.map((mode) => (
            <SelectItem key={mode.value} value={mode.value}>
              {mode.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        type="number"
        min={1}
        value={localCount}
        onChange={handleCountChange}
        onBlur={handleCountBlur}
        aria-label="Message count"
        className="w-20"
      />

      <span className="text-sm text-coval-text-muted">messages</span>
    </div>
  )
}
